import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioAgeGroupServiceObject from '@/entities/ratioAgeGroupService/interface'

import {
  ALL_COLLECTIVE_ADDONS_QUERY,
  SINGLE_COLLECTIVE_ADDON_QUERY,
  COLLECTIVE_ADDONS_DEPENDENTS_QUERY,
  CREATE_COLLECTIVE_ADDON_MUTATION,
  UPDATE_COLLECTIVE_ADDON_MUTATION,
  DELETE_COLLECTIVE_ADDON_MUTATION,
  FORCE_DELETE_COLLECTIVE_ADDON_MUTATION,
  ADD_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION,
  REMOVE_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION,
  CREATE_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION,
  DELETE_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioCollectiveAddonRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_COLLECTIVE_ADDON_QUERY, variables).then((data: any) => {
      return hydrateObject(data.collectiveAddon)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_COLLECTIVE_ADDONS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.collectiveAddons)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_COLLECTIVE_ADDON_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.collectiveAddon),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_COLLECTIVE_ADDONS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.collectiveAddons),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(COLLECTIVE_ADDONS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([COLLECTIVE_ADDONS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_COLLECTIVE_ADDON_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.collectiveAddon)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_COLLECTIVE_ADDON_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.collectiveAddon)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_COLLECTIVE_ADDON_MUTATION, variables).then((data: any) => {
      return data.deleteRatioCollectiveAddon
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_COLLECTIVE_ADDON_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioCollectiveAddon
    })
  },

  assignAgeGroupServices: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupServiceIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION, variables)
  },

  unassignAgeGroupServices: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupServiceIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION, variables)
  },

  createAgeGroupServices: async (id: string, items: Array<ratioAgeGroupServiceObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupServices: items,
      },
    }
    return graphQLClient.request(CREATE_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION, variables)
  },

  deleteAgeGroupServices: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupServiceIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_COLLECTIVE_ADDON_AGE_GROUP_SERVICES_MUTATION, variables)
  },
}

export default ratioCollectiveAddonRepository
